/* eslint-disable max-lines-per-function */
import {
  Body,
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@e2n/ui/v2';
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { BerufeSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplan/components/BerufeSelectV2';
import { useCreateRotationsplanVorlage } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useCreateRotationsplanVorlage/useCreateRotationsplanVorlage';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import z from 'zod';

export function CreateRotationsplanVorlage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createRotationsplanVorlage] = useCreateRotationsplanVorlage();
  const { currentStandort } = useStandortContext();

  const schema = z.object({
    beruf: z.instanceof(Beruf, {
      message: t('generalValidationError.required'),
    }),
    titel: z
      .string()
      .trim()
      .min(1, { message: t('generalValidationError.required') }),
  });

  const form = useForm({
    defaultValues: {
      beruf: undefined as unknown as Beruf,
      titel: '',
    },
    validators: {
      onChange: schema,
    },
    onSubmit: async ({ value }: { value: { beruf: Beruf; titel: string } }) => {
      const showError = () =>
        toast({
          variant: 'destructive',
          title: t(
            'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.error',
          ),
        });

      try {
        const result = await createRotationsplanVorlage({
          input: {
            berufId: value.beruf.id,
            titel: value.titel.trim(),
            standortId: currentStandort.uid,
          },
        });

        if (!result.data?.mitarbeiter_createRotationsplanVorlage.id) {
          showError();
          return;
        }

        toast({
          title: t(
            'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.success',
          ),
        });
        navigate('/mitarbeiter/rotationsplaene/vorlagen', { replace: true });
      } catch (error) {
        Sentry.captureException(error);
        showError();
      }
    },
  });

  const berufLabel = t(
    'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.fields.beruf.label',
  );

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) {
          form.reset();
          navigate('/mitarbeiter/rotationsplaene/vorlagen', {
            replace: true,
          });
        }
      }}>
      <SheetContent
        aria-describedby={undefined}
        className="data-[side=right]:w-full sm:data-[side=right]:max-w-130">
        <SheetHeader>
          <SheetTitle className="flex-1">
            {t('domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.titel')}
          </SheetTitle>
          <Button
            className="md:hidden"
            disabled={!form.state.isValid || form.state.isSubmitting}
            form="createVorlageForm">
            {t('actions.fertig')}
          </Button>
        </SheetHeader>
        <SheetBody className="flex flex-col gap-4 sm:p-6">
          <Body size="sm" className="text-labels-secondary">
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.hinweisPflichtfelder',
            )}
          </Body>
          <form
            id="createVorlageForm"
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              form.handleSubmit();
            }}>
            <form.Field name="beruf">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel
                      htmlFor={field.name}
                      invalid={isInvalid}
                      requiredField>
                      {berufLabel}
                    </FieldLabel>
                    <BerufeSelect
                      handleChange={field.handleChange}
                      id={field.name}
                      invalid={isInvalid}
                      value={field.state.value}
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            </form.Field>
            <form.Field name="titel">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel
                      htmlFor={field.name}
                      invalid={isInvalid}
                      requiredField>
                      {t(
                        'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.fields.titel.label',
                      )}
                    </FieldLabel>
                    <Input
                      id={field.name}
                      aria-invalid={isInvalid}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      placeholder={t(
                        'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.fields.titel.placeholder',
                      )}
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            </form.Field>
          </form>
        </SheetBody>
        <SheetFooter className="hidden md:flex">
          <Button
            disabled={!form.state.isValid || form.state.isSubmitting}
            form="createVorlageForm">
            {t('actions.fertig')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
